import sanitize from 'sanitize-html'

// ── SHARED WHITELIST — keep in sync with
//    progsity-backend/src/shared/validation/sanitize-html.ts
//    progsity-admin/src/lib/sanitize-lesson-html.ts
//    (no shared package yet per docs/00_PRODUCT_MAP.md).
//
// Any edit here MUST be mirrored in the other copies in the same commit.
// ─────────────────────────────────────────────────────────────────────────
const ALLOWED_TAGS = [
  'p', 'h1', 'h2', 'h3', 'h4', 'strong', 'em', 'u', 's', 'sub', 'sup',
  'ul', 'ol', 'li', 'a', 'br', 'hr',
  'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'figure', 'figcaption',
  'video', 'iframe',
  'div', 'span',
  'mark',
  'label',
  // A resized table's column widths (`col` carries them as a px width).
  'colgroup', 'col',
]

const ALLOWED_ATTR = [
  'class', 'href', 'src', 'alt', 'width', 'height', 'target', 'rel',
  'data-*',
  'autoplay', 'loop', 'muted', 'playsinline', 'controls',
  'allow', 'allowfullscreen', 'frameborder',
  'colspan', 'rowspan',
  // Never passed whole: ALLOWED_STYLES below keeps a declaration only when its
  // property and value are on the list, and drops the attribute when none is.
  'style',
]

// ── Inline SVG diagrams in lesson content (`LN-SEED01`, STYLE_GUIDE.md §4) ──
// A closed set: what a hand-drawn teaching diagram needs, and nothing that can
// pull in or run anything else.
//   - never `foreignObject`: it re-opens arbitrary HTML inside the SVG;
//   - never `use`, `href` or `xlink:href` on an SVG node: same-document and
//     external references;
//   - never an `on*` handler: it is not on the attribute list, and
//     sanitize-html drops every attribute that is not.
const ALLOWED_SVG_TAGS = [
  'svg', 'path', 'rect', 'circle', 'line', 'polyline', 'polygon',
  'g', 'text', 'tspan', 'defs', 'marker', 'title',
]

// htmlparser2 lower-cases attribute names before sanitize-html sees them, so
// the camelCase SVG attributes are listed in both spellings; the browser's own
// HTML parser maps `viewbox` back to `viewBox` for inline SVG when it renders.
const ALLOWED_SVG_ATTR = [
  'viewBox', 'viewbox', 'd', 'x', 'y', 'x1', 'y1', 'x2', 'y2',
  'cx', 'cy', 'r', 'rx', 'ry', 'width', 'height', 'points',
  'fill', 'stroke', 'stroke-width', 'stroke-dasharray', 'marker-end', 'marker-start',
  'text-anchor', 'font-size', 'font-weight', 'transform',
  'role', 'aria-label',
]

// `id` is allowed on `marker` and on nothing else (`LN-SEED02`): `marker-end`
// and `marker-start` name their definition with `url(#name)`, so an arrowhead
// cannot work without one. An SVG element's `id` takes no part in the window's
// named-element lookup, so it opens no DOM-clobbering surface; every other SVG
// child, and every HTML tag, stays without `id`.
const SVG_ATTR_MAP: Record<string, string[]> = Object.fromEntries(
  ALLOWED_SVG_TAGS.map((tag) => [
    tag,
    tag === 'marker' ? [...ALLOWED_SVG_ATTR, 'id'] : ALLOWED_SVG_ATTR,
  ]),
)

// If Progsity starts using a Bunny player embed with a different host (e.g. a
// custom CNAME), this list MUST be updated in both copies of the whitelist.
const ALLOWED_IFRAME_HOSTNAMES = [
  'iframe.mediadelivery.net',
  'www.youtube-nocookie.com',
  'www.youtube.com',
]

// ── Inline styles: what the editor writes, and nothing else (ADR-0108) ──
// Text colour, highlight and alignment from the editor's toolbar, and a
// resized table's widths. Every value is matched whole: a colour is a hex,
// rgb() or hsl() value, a width is a px number, so no declaration can load a
// resource (`url()`), run anything (`expression()`) or move an element over
// the page (`position` is not on the list). Until 2026-10-03 `style` was not
// allowed at all, and every colour an author picked was lost on save.
const COLOUR = [
  /^#[0-9a-f]{3,8}$/i,
  /^rgba?\(\s*\d{1,3}%?\s*,\s*\d{1,3}%?\s*,\s*\d{1,3}%?\s*(,\s*(0|1|0?\.\d+))?\s*\)$/i,
  /^hsla?\(\s*\d{1,3}(deg)?\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*(,\s*(0|1|0?\.\d+))?\s*\)$/i,
]
const PX = [/^\d{1,4}(\.\d+)?px$/]

const ALLOWED_STYLES = {
  '*': {
    // `inherit` is what a highlight writes for its text colour.
    color: [...COLOUR, /^inherit$/],
    'background-color': COLOUR,
    'text-align': [/^(left|right|center|justify)$/],
  },
  table: { width: PX, 'min-width': PX },
  col: { width: PX, 'min-width': PX },
}

const SANITIZE_OPTIONS = {
  allowedTags: [...ALLOWED_TAGS, ...ALLOWED_SVG_TAGS],
  // `colwidth` is how the editor reloads a resized column; it is never CSS.
  allowedAttributes: { '*': ALLOWED_ATTR, td: ['colwidth'], th: ['colwidth'], ...SVG_ATTR_MAP },
  allowedStyles: ALLOWED_STYLES,
  allowedIframeHostnames: ALLOWED_IFRAME_HOSTNAMES,
}

export function sanitizeHtml(dirty: string): string {
  if (!dirty) return ''
  return sanitize(dirty, SANITIZE_OPTIONS)
}
